import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteConfirmModal } from '@/components/ui';
import { UserGroupService } from '@/services/userGroups.service';
import { useNotificationStore } from '@/store/notificationStore';
import type { UserGroup } from '@/types/userGroups.types';

interface SortableUserGroupItemProps {
  group: UserGroup;
  isOpen: boolean;
  isDeleting: boolean;
  onToggleMenu: (groupId: number) => void;
  onEdit: (group: UserGroup) => void;
  onDelete: (group: UserGroup) => void;
}

const SortableUserGroupItem: React.FC<SortableUserGroupItemProps> = ({
  group,
  isOpen,
  isDeleting,
  onToggleMenu,
  onEdit,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
  });

  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192, // 192px = w-48 in Tailwind
      });
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx('relative flex-shrink-0', !group.isActive && 'opacity-60')}
      {...attributes}
    >
      <div className="group" style={{ minWidth: '120px', maxWidth: '160px', width: '140px' }}>
        <div className="flex items-center justify-center gap-1">
          <button
            {...listeners}
            className="flex-shrink-0 cursor-grab touch-none rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:text-gray-600 active:cursor-grabbing group-hover:opacity-100 dark:hover:text-gray-300"
            title="Drag to reorder"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8h16M4 16h16"
              />
            </svg>
          </button>
          <div className="flex flex-1 flex-col items-center px-1">
            <div
              className="w-full break-words text-center text-xs font-semibold uppercase leading-tight tracking-wider text-gray-900 dark:text-white"
              title={group.name}
            >
              {group.name}
            </div>
            {!group.isActive && (
              <span className="mt-0.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-gray-500 dark:bg-dark-600 dark:text-gray-400">
                Inactive
              </span>
            )}
          </div>
          <button
            ref={menuButtonRef}
            onClick={() => onToggleMenu(group.id)}
            className="flex-shrink-0 rounded p-0.5 text-gray-500 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100 dark:text-gray-400 dark:hover:bg-dark-700 dark:hover:text-gray-300"
            title="More actions"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Dropdown Menu - Positioned outside overflow container using fixed positioning */}
      {isOpen && menuPosition && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => onToggleMenu(group.id)} />
          <div
            className="fixed z-[101] w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-dark-600 dark:bg-dark-700"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <div className="py-1">
              <button
                onClick={() => onEdit(group)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit Group
              </button>
              <button
                onClick={() => onDelete(group)}
                disabled={isDeleting}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 disabled:opacity-50 dark:text-red-400 dark:hover:bg-dark-600"
              >
                {isDeleting ? (
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    />
                    <path
                      fill="currentColor"
                      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      className="opacity-75"
                    />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                )}
                Delete Group
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface UserGroupHeadingProps {
  userGroups: UserGroup[];
  isLoading: boolean;
  onDelete: (userGroupName: string) => void;
  onEdit: (userGroup: UserGroup) => void;
  onReorderSuccess?: () => void;
}

/**
 * UserGroupHeading - Header component showing user group columns with actions
 */
export const UserGroupHeading: React.FC<UserGroupHeadingProps> = ({
  userGroups,
  isLoading,
  onDelete,
  onEdit,
  onReorderSuccess,
}) => {
  const { addNotification } = useNotificationStore();
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [orderedGroups, setOrderedGroups] = useState<UserGroup[]>(userGroups);
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<UserGroup | null>(null);

  useEffect(() => {
    setOrderedGroups(userGroups);
  }, [userGroups]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedGroups.findIndex((group) => group.id === active.id);
    const newIndex = orderedGroups.findIndex((group) => group.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const previousGroups = orderedGroups;
    const reorderedGroups = arrayMove(orderedGroups, oldIndex, newIndex);

    setOrderedGroups(reorderedGroups);

    try {
      const sortedIds = reorderedGroups.map((group) => group.id);
      await UserGroupService.sortUserGroups({ userGroupIds: sortedIds });

      onReorderSuccess?.();
    } catch (error) {
      setOrderedGroups(previousGroups);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification({
        type: 'error',
        title: 'Reorder Failed',
        message: errorMessage,
      });
    }
  };

  const handleRequestDeleteGroup = (userGroup: UserGroup) => {
    setOpenMenuId(null);
    setPendingDeleteGroup(userGroup);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!pendingDeleteGroup) {
      return;
    }

    setDeletingId(pendingDeleteGroup.id);
    try {
      await UserGroupService.deleteUserGroup(pendingDeleteGroup.id);
      onDelete(pendingDeleteGroup.name);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage,
      });
    } finally {
      setDeletingId(null);
      setOpenMenuId(null);
      setPendingDeleteGroup(null);
    }
  };

  const handleEditGroup = (userGroup: UserGroup) => {
    setOpenMenuId(null);
    onEdit(userGroup);
  };

  const toggleMenu = (groupId: number) => {
    setOpenMenuId(openMenuId === groupId ? null : groupId);
  };

  if (isLoading) {
    return (
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 dark:border-dark-600 dark:bg-dark-800">
        <div className="flex items-center">
          <div
            className="sticky left-0 z-30 flex-shrink-0 bg-gray-50 px-4 py-3 dark:bg-dark-800"
            style={{ minWidth: '280px' }}
          >
            <div className="h-5 w-32 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
          </div>
          <div className="flex flex-1 items-center gap-4 px-4 py-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex-shrink-0" style={{ width: '140px' }}>
                <div className="h-5 w-full animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
              </div>
            ))}
          </div>
          <div className="sticky right-0 z-30 w-20 flex-shrink-0 bg-gray-50 py-3 dark:bg-dark-800" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sticky top-0 z-20 border-b-2 border-gray-200 bg-gray-50 dark:border-dark-600 dark:bg-dark-800">
        <div className="flex items-center">
          {/* Rights Label Column - Sticky Left */}
          <div
            className="sticky left-0 z-30 flex-shrink-0 bg-gray-50 px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 dark:bg-dark-800 dark:text-gray-400"
            style={{ minWidth: '280px' }}
          >
            Rights
          </div>

          {/* User Group Columns - Draggable */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedGroups.map((g) => g.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex flex-1 items-center gap-4 px-4 py-3">
                {orderedGroups.map((group) => (
                  <SortableUserGroupItem
                    key={group.id}
                    group={group}
                    isOpen={openMenuId === group.id}
                    isDeleting={deletingId === group.id}
                    onToggleMenu={toggleMenu}
                    onEdit={handleEditGroup}
                    onDelete={handleRequestDeleteGroup}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Actions Column Spacer - Sticky Right */}
          <div className="sticky right-0 z-30 w-20 flex-shrink-0 bg-gray-50 py-3 dark:bg-dark-800" />
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={!!pendingDeleteGroup}
        onClose={() => setPendingDeleteGroup(null)}
        onConfirm={handleDeleteGroupConfirm}
        loading={deletingId === pendingDeleteGroup?.id}
        title="Delete User Group"
        message="Are you sure you want to delete this user group? This action cannot be undone."
        itemName={pendingDeleteGroup?.name}
      />
    </>
  );
};
